<script setup lang="ts">
import { Plus } from 'lucide-vue-next'
import { useRoute } from 'vue-router'
import { useTodoStream } from '@/composables/useTodoStream'
import { useTicketNotifications } from '@/composables/useTicketNotifications'
import { useTabNavigation } from '@/composables/useTabNavigation'
import type { Pillar, NavItem } from '@/composables/useNavigation'

const props = defineProps<{
  pillar: Pillar | null
  show: boolean
}>()

const route = useRoute()
const { badgeCount: todoBadgeCount } = useTodoStream()
const { newTicketCount } = useTicketNotifications()
const { openInNewTab } = useTabNavigation()

function isActiveRoute(path: string): boolean {
  return path === '/' ? route.path === '/' : route.path.startsWith(path)
}

function getBadgeCount(item: NavItem): number {
  if (item.badge === 'todo') return todoBadgeCount.value
  if (item.badge === 'ticket') return newTicketCount.value
  return 0
}

function getBadgeColor(item: NavItem): string {
  if (item.badge === 'ticket') return 'panel-badge-danger'
  return 'panel-badge'
}

function handleOpenInTab(e: Event, item: NavItem) {
  e.preventDefault()
  e.stopPropagation()
  openInNewTab(item.path, item.name)
}
</script>

<template>
  <Transition name="panel-slide">
    <div v-if="props.show && props.pillar" class="nav-panel">
      <div class="panel-header">
        <h2 class="panel-title">{{ props.pillar.label }}</h2>
      </div>
      <nav class="panel-links">
        <div
          v-for="item in props.pillar.items"
          :key="item.path"
          class="panel-link-row group"
        >
          <router-link
            :to="item.path"
            class="panel-link"
            :class="{ active: isActiveRoute(item.path) }"
          >
            <component :is="item.icon" class="w-5 h-5 flex-shrink-0" />
            <span class="flex-1">{{ item.name }}</span>
            <span
              v-if="getBadgeCount(item) > 0"
              :class="getBadgeColor(item)"
            >
              {{ getBadgeCount(item) > 99 ? '99+' : getBadgeCount(item) }}
            </span>
          </router-link>
          <button
            class="tab-open-btn"
            title="Open in new tab"
            @click="handleOpenInTab($event, item)"
          >
            <Plus class="w-4 h-4" />
          </button>
        </div>
      </nav>
    </div>
  </Transition>
</template>
